/**
 * Held card payments and unconfirmed payment links (SPEC-invoice-merge-history R5 iteration 4).
 *
 * HeldPaymentsBanner       ADV-R5-04: a card payment that arrived but could not be told apart from an earlier
 *                          receipt is NOT added to the invoice until staff decide. Shown in the payment panel.
 * ReservationReconcileList ADV-R5-05: a payment link whose result was never confirmed blocks a merge or a payer
 *                          change. "Check with payment provider" asks the provider and settles it; time alone
 *                          never does.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  HOLD_OUTCOME_LABELS,
  formatHeldAmount,
  isSettled,
  type BlockingReservation,
  type HeldPayment,
  type HoldOutcome,
  type ReconcileResult,
} from '@/lib/invoice-payment-reconciliation';

const failureMessage = (cause: unknown, fallback: string): string => {
  const failure = cause as { data?: { error?: { message?: string }; detail?: { message?: string } }; message?: string };
  return failure?.data?.error?.message ?? failure?.data?.detail?.message ?? failure?.message ?? fallback;
};

function HeldPaymentCard({ invoiceId, hold }: { invoiceId: string; hold: HeldPayment }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<HoldOutcome | ''>('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: () =>
      api.invoices.resolvePaymentHold(invoiceId, hold.hold_id, {
        outcome: outcome as HoldOutcome,
        note: note.trim(),
        version: hold.version,
      }),
    onSuccess: async () => {
      setMessage(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId, 'payment-holds'] }),
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId, 'payments'] }),
        queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
      ]);
    },
    onError: async (cause) => {
      const code = (cause as { data?: { error?: { code?: string }; detail?: { code?: string } } })?.data;
      const known = code?.error?.code ?? code?.detail?.code;
      setMessage(
        known === 'hold_changed_review_again'
          ? 'This payment or the invoice changed since you opened it. It was reloaded below. Read it again before deciding.'
          : failureMessage(cause, 'The decision was not saved. Nothing was changed.'),
      );
      await queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId, 'payment-holds'] });
    },
  });

  const earlier = hold.legacy_payments;
  const ready = outcome !== '' && note.trim().length >= 3 && !resolve.isPending;
  return (
    <div className="space-y-2 rounded border border-amber-400 bg-white p-3 text-sm" data-testid={`held-payment-${hold.hold_id}`}>
      <p>
        <strong>{formatHeldAmount(hold.amount)}</strong> was paid by card (payment {hold.provider_payment_intent_id}) but is not
        on this invoice yet. An earlier card receipt for the same amount
        {earlier.length > 0
          ? ` (${earlier.map((p) => `${formatHeldAmount(p.amount)} on ${p.paid_at ? p.paid_at.slice(0, 10) : 'an unknown date'}`).join('; ')})`
          : ''}{' '}
        may be the same payment, so nothing was added automatically.
      </p>
      <fieldset className="space-y-1">
        <legend className="font-medium">What is this payment?</legend>
        {(Object.keys(HOLD_OUTCOME_LABELS) as HoldOutcome[]).map((value) => (
          <label key={value} className="flex items-start gap-2">
            <input
              type="radio"
              name={`hold-outcome-${hold.hold_id}`}
              value={value}
              checked={outcome === value}
              onChange={() => setOutcome(value)}
              data-testid={`hold-outcome-${value}`}
            />
            <span>{HOLD_OUTCOME_LABELS[value]}</span>
          </label>
        ))}
      </fieldset>
      <label className="block">
        <span className="font-medium">Reason (kept with the record)</span>
        <textarea
          className="mt-1 w-full rounded border p-2"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          data-testid="hold-note"
        />
      </label>
      {message && <p role="alert" className="text-destructive" data-testid="hold-message">{message}</p>}
      <Button size="sm" disabled={!ready} onClick={() => resolve.mutate()} data-testid="hold-resolve-button">
        {resolve.isPending ? 'Saving...' : 'Save decision'}
      </Button>
    </div>
  );
}

export function HeldPaymentsBanner({ invoiceId }: { invoiceId: string }) {
  const query = useQuery({
    queryKey: ['invoice', invoiceId, 'payment-holds'],
    queryFn: () => api.invoices.paymentHolds(invoiceId),
  });
  const holds = query.data ?? [];
  if (holds.length === 0) return null;
  return (
    <div role="alert" className="mb-3 space-y-2 rounded border border-amber-500 bg-amber-50 p-3" data-testid="held-payments-banner">
      <p className="font-semibold">
        {holds.length === 1 ? 'A card payment is waiting for your decision' : `${holds.length} card payments are waiting for your decision`}
      </p>
      {holds.map((hold) => (
        <HeldPaymentCard key={hold.hold_id} invoiceId={invoiceId} hold={hold} />
      ))}
    </div>
  );
}

function ReservationRow({ reservation, onSettled }: { reservation: BlockingReservation; onSettled: (r: ReconcileResult) => void }) {
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invoiceId = reservation.requested_invoice_id ?? reservation.invoice_id;

  async function check(cancelOpenIntent: boolean) {
    setBusy(true);
    setError(null);
    try {
      const out = await api.invoices.reconcileCheckoutReservation(invoiceId, reservation.reservation_id, {
        cancel_open_intent: cancelOpenIntent,
      });
      setResult(out);
      if (isSettled(out.outcome)) onSettled(out);
    } catch (cause) {
      setError(failureMessage(cause, 'The payment provider could not be checked. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  const started = reservation.created_at ? new Date(reservation.created_at).toLocaleString('en-US') : null;
  return (
    <div className="space-y-1" data-testid={`reservation-${reservation.reservation_id}`}>
      <p>
        A payment link{started ? ` started ${started}` : ''} has no confirmed result ({reservation.state === 'UNKNOWN' ? 'the connection dropped' : 'it never finished'}).
      </p>
      {result && <p data-testid="reservation-result">{result.message}</p>}
      {error && <p role="alert" className="text-destructive" data-testid="reservation-error">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void check(false)} data-testid="reservation-check-button">
          {busy ? 'Checking...' : 'Check with payment provider'}
        </Button>
        {result?.outcome === 'still_blocked' && result.can_cancel && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void check(true)} data-testid="reservation-cancel-button">
            Cancel the open payment
          </Button>
        )}
      </div>
    </div>
  );
}

export function ReservationReconcileList({
  reservations,
  onSettled,
}: {
  reservations: BlockingReservation[];
  onSettled: (result: ReconcileResult) => void;
}) {
  if (reservations.length === 0) return null;
  return (
    <div className="space-y-3 rounded border p-3 text-sm" data-testid="reservation-reconcile-list">
      <p className="font-medium">Unconfirmed payment links</p>
      {reservations.map((reservation) => (
        <ReservationRow key={reservation.reservation_id} reservation={reservation} onSettled={onSettled} />
      ))}
    </div>
  );
}
